/**
 * Parenthesised positions whose owner word fixes what may appear inside.
 *
 * `JOIN … USING (▮)` takes column NAMES — not the tables those columns live in.
 * `GROUP BY ROLLUP(▮)` and `CUBE(▮)` take grouping expressions. Oracle's
 * `SAMPLE (▮)` takes a percentage and `PARTITION (▮)` a partition name, neither
 * of which the schema can supply. Every one of them was answered with the
 * resolver's full emission — hundreds of tables, schemas and FK-join snippets
 * at a caret where a table name is a syntax error.
 *
 * This is a post-filter rather than a slot because the answer is not a keyword
 * list: at `USING (▮)` the columns the resolver already emitted ARE the right
 * answer, and only the rest has to go.
 */

import type { CompletionCandidate, DatabaseEngineId } from '@/core/engines';

import { parenHeadScanEnd } from '../context/clauseShapeSlot';
import { scanRegion, wordBefore, scanOptionsFor } from '../context/slotTextScan';

/** What a paren admits: only column-shaped operands, or nothing the schema
 *  knows about. */
type ParenScope = 'operands-only' | 'nothing';

/** Owner word → what its paren admits. */
const PAREN_SCOPES: Readonly<Record<string, ParenScope>> = {
  USING: 'operands-only',
  ROLLUP: 'operands-only',
  CUBE: 'operands-only',
  // `GROUPING SETS (` — the scan's last word before `(` is SETS.
  SETS: 'operands-only',
  SAMPLE: 'nothing',
  PARTITION: 'nothing',
};

/** Candidate kinds that can stand where an operand is wanted. */
const OPERAND_KINDS: ReadonlySet<CompletionCandidate['kind']> = new Set<
  CompletionCandidate['kind']
>(['column', 'alias', 'variable', 'parameter']);

export function applyParenScopeFilter(
  candidates: CompletionCandidate[],
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): CompletionCandidate[] {
  const scope = parenScopeAt(text, offset, statementStart, engine);
  if (scope === null) return candidates;
  if (scope === 'nothing') return [];
  return candidates.filter((c) => OPERAND_KINDS.has(c.kind));
}

function parenScopeAt(
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): ParenScope | null {
  const opts = scanOptionsFor(engine);
  const scanEnd = parenHeadScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const openAt = scanRegion(text, statementStart, scanEnd, opts).openParenAt;
  if (openAt === null) return null;
  const owner = wordBefore(text, openAt, statementStart);
  const scope = PAREN_SCOPES[owner];
  if (scope === undefined) return null;
  // `MERGE … USING (SELECT …)` is a source RELATION, not a column list — the
  // one `USING` whose paren admits exactly what this filter would strip.
  if (owner === 'USING' && scanRegion(text, statementStart, openAt, opts).tokens[0]?.word === 'MERGE') {
    return null;
  }
  return scope;
}
